import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router";

/**
 * Хук для отслеживания секретных кликов для доступа к feature flags
 * После 10 кликов на определенный элемент переходит на /feature-flags
 */
export const useSecretAccess = (
  targetPath = "/feature-flags",
  maxClicks = 10
) => {
  const navigate = useNavigate();
  const clickCountRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isActivating, setIsActivating] = useState(false);
  const [currentClicks, setCurrentClicks] = useState(0);

  const handleSecretClick = useCallback(() => {
    clickCountRef.current += 1;
    const newClickCount = clickCountRef.current;
    setCurrentClicks(newClickCount);

    // Сброс счетчика через 5 секунд бездействия
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      clickCountRef.current = 0;
      setCurrentClicks(0);
    }, 5000);

    // Если достигли максимального количества кликов
    if (newClickCount >= maxClicks) {
      clickCountRef.current = 0;
      setCurrentClicks(0);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      setIsActivating(true);

      // Небольшая задержка для эффектности
      setTimeout(() => {
        setIsActivating(false);
        navigate(targetPath);
      }, 500);

      return true; // Указывает, что секрет активирован
    }

    return false;
  }, [navigate, targetPath, maxClicks]);

  // Функция для получения текущего количества кликов (для дебага)
  const getCurrentClicks = useCallback(() => clickCountRef.current, []);

  return {
    handleSecretClick,
    getCurrentClicks,
    isActivating,
    clicksRemaining: maxClicks - currentClicks,
    currentClicks,
  };
};
